import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_OPTIONS = [
  "Rice",
  "Onion",
  "Foxnuts",
  "Spices",
  "Fruits",
  "Eggs",
  "Water",
  "Other / General Inquiry",
];

const INFO = [
  {
    icon: Phone,
    label: "Phone",
    value: "+91 6398888926",
    href: "tel:+916398888926",
    testid: "contact-phone-link",
  },
  {
    icon: Mail,
    label: "Email",
    value: "info@neshaglobal.com",
    href: "mailto:info@neshaglobal.com",
    testid: "contact-email-link",
  },
  {
    icon: MapPin,
    label: "Registered Office",
    value: "Langha Road, Vikasnagar, C/O Amit Kumar Chharba, Sahaspur, Dehradun — 248197, Uttarakhand, India",
    href: "https://www.google.com/maps/search/?api=1&query=Chharba+Sahaspur+Dehradun+248197+Uttarakhand",
    testid: "contact-address-link",
  },
  {
    icon: Clock,
    label: "Working Hours",
    value: "Mon – Sat: 08:00 – 20:00 IST (24/7 sea-cargo support)",
    testid: "contact-hours",
  },
];

const inputCls =
  "w-full rounded-xl bg-cream/5 border border-cream/15 px-4 py-3.5 text-sm text-cream placeholder:text-cream/35 outline-none focus:border-sunset focus:bg-cream/10 transition-colors duration-300";

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    product: PRODUCT_OPTIONS[0],
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setSent(true);
      toast.success("Enquiry received — our trade desk will reply within 4 hours.");
      setForm({ name: "", email: "", phone: "", product: PRODUCT_OPTIONS[0], message: "" });
      setTimeout(() => setSent(false), 6000);
    } catch {
      toast.error("Could not send your enquiry. Please email info@neshaglobal.com.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-mud grain py-20 sm:py-28 overflow-hidden"
    >
      <div
        className="absolute -top-32 right-0 w-[30rem] h-[30rem] rounded-full opacity-15 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, #E09F3E 0%, transparent 70%)" }}
      />
      <div className="relative z-10 px-6 sm:px-12 lg:px-20 max-w-7xl mx-auto">
        <div className="max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-[10px] sm:text-xs tracking-[0.4em] text-sunset uppercase mb-5"
          >
            04 — Contact Us
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl text-cream tracking-tight leading-[1.08]"
          >
            Connect with our <span className="italic text-gold">global trade desk.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-5 text-sm sm:text-base leading-relaxed text-cream/60"
          >
            Whether you require a full container load of grain or specialised air
            freight for spices, our experts respond within 4 hours.
          </motion.p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* info column */}
          <div className="lg:col-span-5 space-y-4">
            {INFO.map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, delay: i * 0.1 }}
                className="flex gap-5 rounded-2xl border border-cream/12 bg-forest/40 p-6"
              >
                <span className="shrink-0 w-12 h-12 rounded-full bg-sunset/15 text-sunset flex items-center justify-center">
                  <item.icon className="w-5 h-5" />
                </span>
                <div className="min-w-0">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-cream/45 uppercase">
                    {item.label}
                  </p>
                  {item.href ? (
                    <a
                      data-testid={item.testid}
                      href={item.href}
                      target={item.href.startsWith("http") ? "_blank" : undefined}
                      rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                      className="mt-1.5 block text-sm sm:text-base leading-relaxed text-cream hover:text-gold transition-colors duration-300 break-words"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <p
                      data-testid={item.testid}
                      className="mt-1.5 text-sm sm:text-base leading-relaxed text-cream"
                    >
                      {item.value}
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* form column */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7"
          >
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="rounded-2xl border border-cream/12 bg-forest/40 p-6 sm:p-10"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="cf-name" className="font-mono text-[10px] tracking-[0.3em] uppercase text-cream/50">
                    Full Name *
                  </label>
                  <input
                    id="cf-name"
                    data-testid="contact-input-name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name"
                    className={`${inputCls} mt-2`}
                  />
                </div>
                <div>
                  <label htmlFor="cf-email" className="font-mono text-[10px] tracking-[0.3em] uppercase text-cream/50">
                    Email *
                  </label>
                  <input
                    id="cf-email"
                    data-testid="contact-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@company.com"
                    className={`${inputCls} mt-2`}
                  />
                </div>
                <div>
                  <label htmlFor="cf-phone" className="font-mono text-[10px] tracking-[0.3em] uppercase text-cream/50">
                    Phone
                  </label>
                  <input
                    id="cf-phone"
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+91 ..."
                    className={`${inputCls} mt-2`}
                  />
                </div>
                <div>
                  <label htmlFor="cf-product" className="font-mono text-[10px] tracking-[0.3em] uppercase text-cream/50">
                    Product of Interest
                  </label>
                  <select
                    id="cf-product"
                    data-testid="contact-select-product"
                    value={form.product}
                    onChange={set("product")}
                    className={`${inputCls} mt-2 appearance-none cursor-pointer`}
                  >
                    {PRODUCT_OPTIONS.map((o) => (
                      <option key={o} value={o} className="bg-forest text-cream">
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cf-message" className="font-mono text-[10px] tracking-[0.3em] uppercase text-cream/50">
                    Your Requirement *
                  </label>
                  <textarea
                    id="cf-message"
                    data-testid="contact-input-message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Quantity, destination port, incoterms, timeline..."
                    className={`${inputCls} mt-2 resize-none`}
                  />
                </div>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-5">
                <button
                  data-testid="contact-form-submit"
                  type="submit"
                  disabled={sending}
                  className="group inline-flex items-center gap-3 rounded-full bg-sunset px-8 py-4 font-grotesk text-sm font-semibold uppercase tracking-wider text-forest hover:bg-sunset-bright transition-colors duration-300 disabled:opacity-60"
                >
                  {sending ? "Sending..." : "Send Enquiry"}
                  <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-0.5 transition-transform duration-300" />
                </button>
                {sent && (
                  <p
                    data-testid="contact-success-message"
                    className="inline-flex items-center gap-2 text-sm text-sky-light"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Enquiry received. We will reply within 4 hours.
                  </p>
                )}
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
